import { PrivateUserInfoType, Vehicle } from '@otw/models'
import { useQuery } from '@tanstack/react-query'
import { db, storage } from 'config/firebase'
import {
  photoUploadClientConverter
} from '@otw/models/converters'
import { PhotoUploadClient } from '@otw/models/photos'
import { 
  FirestoreDataConverter, 
  DocumentSnapshot, 
  SnapshotOptions, 
  WithFieldValue 
} from 'firebase/firestore'

// Photo Upload Document interface (keeping local for backward compatibility)
export interface PhotoUploadDocument {
  id: string
  userId: string
  vehicleIds: string[]
  submittedVehicles: string[]
  createdAt: string
  updatedAt: string
  status: 'active' | 'completed' | 'cancelled'
  accessCode?: string
  expiresAt?: string
  completedAt?: string
}

// Photo Upload Document Converter (keeping local for backward compatibility)
export const photoUploadDocumentConverter: FirestoreDataConverter<PhotoUploadDocument> = {
  toFirestore(photoUpload: WithFieldValue<PhotoUploadDocument>) {
    const { id, ...data } = photoUpload
    return {
      ...data,
      createdAt: data.createdAt || new Date().toISOString(),
      updatedAt: data.updatedAt || new Date().toISOString(),
      status: data.status || 'active',
      vehicleIds: data.vehicleIds || [],
      submittedVehicles: data.submittedVehicles || []
    }
  },
  fromFirestore(snapshot: DocumentSnapshot, options: SnapshotOptions): PhotoUploadDocument {
    const data = snapshot.data(options)
    
    if (!data) {
      return {
        id: snapshot.id,
        userId: '',
        vehicleIds: [],
        submittedVehicles: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        status: 'active'
      }
    }

    return {
      id: snapshot.id,
      userId: data.userId || '',
      vehicleIds: data.vehicleIds || [],
      submittedVehicles: data.submittedVehicles || [],
      createdAt: data.createdAt || new Date().toISOString(),
      updatedAt: data.updatedAt || new Date().toISOString(),
      status: data.status || 'active',
      accessCode: data.accessCode || undefined,
      expiresAt: data.expiresAt || undefined,
      completedAt: data.completedAt || undefined
    }
  }
}
import { doc, getDoc } from '@firebase/firestore'
import { getDownloadURL, listAll, ref } from '@firebase/storage'
import { useInventory } from 'hooks/inventory/useInventory'
import { useUsers } from 'hooks/useUsers'

export interface MediaItem {
  id: string
  url: string
  vehicleId: string
  vehicleInfo: string
  uploadedAt?: string
  angle: string
  source: string
  kind: 'photo' | 'video'
}

export interface VehicleWithMedia {
  id: string
  make: string
  model: string
  year: string
  vin: string
  status: 'Submitted'
}

export interface PhotoUploadDetails {
  id: string
  user: PrivateUserInfoType
  vehicles: Vehicle[]
  submittedVehicles: Vehicle[]
  status: string
  createdAt: string
  accessCode?: string
}

async function fetchFolderItems(photoUploadId: string, vehicleId: string, kind: 'photo' | 'video', vehicleInfo: string): Promise<MediaItem[]> {
  const path = kind === 'photo'
    ? `photoUploads/${photoUploadId}/photos/${vehicleId}`
    : `photoUploads/${photoUploadId}/videos/${vehicleId}`
  const r = ref(storage, path)
  const result = await listAll(r)
  const items = await Promise.all(result.items.map(async (item) => {
    try {
      const url = await getDownloadURL(item)
      const baseName = item.name.split('.')[0]
      return {
        id: `${photoUploadId}-${vehicleId}-${kind}-${item.name}`,
        url,
        vehicleId,
        vehicleInfo,
        angle: baseName,
        source: photoUploadId,
        kind,
      } as MediaItem
    } catch {
      return null
    }
  }))
  return items.filter(Boolean) as MediaItem[]
}

export const useMediaUploadViewer = (photoUploadId: string | null) => {
  const { users, isLoading: isLoadingUsers } = useUsers()
  const { vehicles, isLoading: isLoadingVehicles } = useInventory('master_inventory')

  const details = useQuery({
    queryKey: ['mediaUpload-details', photoUploadId],
    queryFn: async (): Promise<PhotoUploadDetails | null> => {
      if (!photoUploadId || !users || !vehicles) return null
      const photoUploadRef = doc(db, 'photoUploads', photoUploadId).withConverter(photoUploadDocumentConverter)
      const photoUploadDoc = await getDoc(photoUploadRef)
      if (!photoUploadDoc.exists()) throw new Error('Photo upload not found')
      const document = photoUploadDoc.data() as PhotoUploadDocument
      const user = users.find(u => u.id === document.userId)
      if (!user) throw new Error('User not found for this photo upload')
      const vehiclesMap = new Map(vehicles.map(v => [v.id, v]))
      const photoUploadVehicles = document.vehicleIds
        .map(vehicleId => vehiclesMap.get(vehicleId))
        .filter((vehicle): vehicle is Vehicle => vehicle !== undefined)
      const submittedVehicles = (document.submittedVehicles || [])
        .map(vehicleId => vehiclesMap.get(vehicleId))
        .filter((vehicle): vehicle is Vehicle => vehicle !== undefined)
      return {
        id: photoUploadId,
        user,
        vehicles: photoUploadVehicles,
        submittedVehicles,
        status: document.status || 'active',
        createdAt: document.createdAt || '',
        accessCode: document.accessCode
      }
    },
    enabled: !!photoUploadId && !!users && !!vehicles,
    staleTime: 5 * 60 * 1000,
  })

  const photos = useQuery({
    queryKey: ['mediaUpload-photos', photoUploadId],
    queryFn: async () => {
      if (!photoUploadId || !vehicles) return [] as MediaItem[]
      const photoUploadRef = doc(db, 'photoUploads', photoUploadId).withConverter(photoUploadDocumentConverter)
      const photoUploadDoc = await getDoc(photoUploadRef)
      if (!photoUploadDoc.exists()) return []
      const vehicleIds = (photoUploadDoc.data() as PhotoUploadDocument).vehicleIds
      const vehiclesMap = new Map(vehicles.map(v => [v.id, v]))
      const all = await Promise.all(vehicleIds.map(async (vehicleId) => {
        const v = vehiclesMap.get(vehicleId)
        if (!v) return [] as MediaItem[]
        const vehicleInfo = `${v.year} ${v.make} ${v.model} (${v.vin})`
        return await fetchFolderItems(photoUploadId, vehicleId, 'photo', vehicleInfo)
      }))
      return all.flat()
    },
    enabled: !!photoUploadId && !!vehicles,
    staleTime: 2 * 60 * 1000,
  })

  const videos = useQuery({
    queryKey: ['mediaUpload-videos', photoUploadId],
    queryFn: async () => {
      if (!photoUploadId || !vehicles) return [] as MediaItem[]
      const photoUploadRef = doc(db, 'photoUploads', photoUploadId).withConverter(photoUploadDocumentConverter)
      const photoUploadDoc = await getDoc(photoUploadRef)
      if (!photoUploadDoc.exists()) return []
      const vehicleIds = (photoUploadDoc.data() as PhotoUploadDocument).vehicleIds
      const vehiclesMap = new Map(vehicles.map(v => [v.id, v]))
      const all = await Promise.all(vehicleIds.map(async (vehicleId) => {
        const v = vehiclesMap.get(vehicleId)
        if (!v) return [] as MediaItem[]
        const vehicleInfo = `${v.year} ${v.make} ${v.model} (${v.vin})`
        return await fetchFolderItems(photoUploadId, vehicleId, 'video', vehicleInfo)
      }))
      return all.flat()
    },
    enabled: !!photoUploadId && !!vehicles,
    staleTime: 2 * 60 * 1000,
  })

  const isLoading = isLoadingUsers || isLoadingVehicles || details.isLoading
  const isLoadingPhotos = photos.isLoading
  const isLoadingVideos = videos.isLoading
  const error = (details.error || photos.error || videos.error)
    ? ((details.error || photos.error || videos.error) as Error).message
    : null

  // Vehicles with any media
  const vehiclesWithMedia: VehicleWithMedia[] = (() => {
    if (!details.data) return []
    const vWith = new Set<string>()
    photos.data?.forEach(p => vWith.add(p.vehicleId))
    videos.data?.forEach(v => vWith.add(v.vehicleId))
    const map = new Map(details.data.vehicles.map(v => [v.id, v]))
    return Array.from(vWith).map(id => {
      const v = map.get(id)!
      return {
        id: id,
        make: v.make || '',
        model: v.model || '',
        year: v.year?.toString() || '',
        vin: v.vin,
        status: 'Submitted' as const,
      }
    })
  })()

  return {
    photoUploadDetails: details.data,
    photos: photos.data || [],
    videos: videos.data || [],
    vehiclesWithMedia,
    isLoading,
    isLoadingPhotos,
    isLoadingVideos,
    error,
  }
}


